import { pollJobAsyncProcess } from 'core/service/job-async-processes/job-async-processes.service';
import { restRequest } from 'core/utils/backend-service';
import type { ActiveDirectoryDetailsItem, ActiveDirectoryListItem } from '../components';
import { LDAP_CONNECTION_REST_ENDPOINT } from '../settings-integrations-active-directory.constants';
import {
    ActiveDirectoryDetailsFormState,
    ActiveDirectoryDetailsItemAPI,
    ActiveDirectoryListItemAPI,
    CheckActiveDirectoryConnectionPayload,
    LdapCheckJobDetail,
} from '../types/active-directory.type';
import {
    mapActiveDirectoryDetailsItemFromApiToVm,
    mapActiveDirectoryDetailsItemFromVmToApi,
    mapActiveDirectoryListItemFromApiToVm,
} from './active-directory.mapper';

export const getActiveDirectoryList = (): Promise<ActiveDirectoryListItem[]> =>
    restRequest('GET', LDAP_CONNECTION_REST_ENDPOINT)
        .then((response) => response.json())
        .then((items: { connections: ActiveDirectoryListItemAPI[] }) =>
            items.connections.map(mapActiveDirectoryListItemFromApiToVm)
        );

export const getActiveDirectoryById = (id: string): Promise<ActiveDirectoryDetailsItem> =>
    restRequest('GET', `${LDAP_CONNECTION_REST_ENDPOINT}/${id}`)
        .then((response) => response.json())
        .then((item: ActiveDirectoryDetailsItemAPI) => mapActiveDirectoryDetailsItemFromApiToVm(item));

export const createActiveDirectory = (
    payload: ActiveDirectoryDetailsFormState,
    identityProviderAttributes: string[]
): Promise<ActiveDirectoryDetailsItem> =>
    restRequest('POST', LDAP_CONNECTION_REST_ENDPOINT, {
        body: mapActiveDirectoryDetailsItemFromVmToApi(payload, identityProviderAttributes),
    })
        .then((response) => response.json())
        .then((item: ActiveDirectoryDetailsItemAPI) => mapActiveDirectoryDetailsItemFromApiToVm(item));

export const updateActiveDirectoryById = (
    id: string,
    payload: ActiveDirectoryDetailsFormState,
    identityProviderAttributes: string[]
): Promise<ActiveDirectoryDetailsItem> =>
    restRequest('PUT', `${LDAP_CONNECTION_REST_ENDPOINT}/${id}`, {
        body: mapActiveDirectoryDetailsItemFromVmToApi(payload, identityProviderAttributes),
    })
        .then((response) => response.json())
        .then((item: ActiveDirectoryDetailsItemAPI) => mapActiveDirectoryDetailsItemFromApiToVm(item));

export const checkActiveDirectoryConnection = async (
    payload: CheckActiveDirectoryConnectionPayload,
    signal?: AbortSignal
): Promise<{
    status: 'success' | 'error';
    errorReason?: string;
    dataPoints: string[];
}> => {
    const { jobValidationId } = await restRequest('POST', `${LDAP_CONNECTION_REST_ENDPOINT}/check`, {
        body: payload,
        signal,
    })
        .then((response) => response.json())
        .then((data: { jobValidationId: string }) => data);

    if (!jobValidationId) {
        throw new Error('Missing job validation id');
    }

    const pollResult = await pollJobAsyncProcess<LdapCheckJobDetail>(jobValidationId, {
        interval: 3000,
        maxAttempts: 20,
        signal,
    });

    const { success, identityProviderAttributes = [], errorReason } = pollResult.detailJob || {};

    if (pollResult.status === 'success' && success && !errorReason) {
        return { status: 'success', dataPoints: identityProviderAttributes };
    }

    return {
        status: 'error',
        errorReason: errorReason || 'Unknown error',
        dataPoints: [],
    };
};
